import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { IAppState } from 'infra/AppState'
import { acceptPushNudge, dismissPushNudge } from 'state/realtime/realtime.actions'
import { pushEvent } from 'state/new-summary/events/events.actions'
import { NEWDASHBOARD_EVENTS, LIVE_UPDATES_ACTION } from 'state/new-summary/events/events.types'
import { NUDGE_BLOCKED_ROUTES } from 'state/realtime/realtime.effects'
import { promptForPushPermission } from 'services/Tracking/FirebaseMessaging'
import { ReactComponent as TickSvg } from 'assets/icons/tick.svg'
import { THEME } from 'components/theme'

/**
 * Contextual "enable live updates" prompt. Shown (via redux) only when web push
 * is supported and permission is still 'default'. Rendered as a full-screen modal
 * with a dimmed backdrop so the user can't miss it. Accepting triggers the browser
 * permission prompt + FCM registration; dismissing is remembered so we don't nag.
 */
const PushNudge: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const visible = useSelector((state: IAppState) => state.Realtime.nudgeVisible)

  // Never show on blocked/utility pages (e.g. subscription-required), even if a
  // redirect landed here while the nudge was already open.
  const onBlockedRoute = NUDGE_BLOCKED_ROUTES.some((path) => pathname.startsWith(path))

  if (!visible || onBlockedRoute) {
    return null
  }

  const onDismiss = () => {
    dispatch(
      pushEvent(NEWDASHBOARD_EVENTS.LIVE_UPDATES, {
        action: LIVE_UPDATES_ACTION.DISMISSED,
        source: 'nudge'
      })
    )
    dispatch(dismissPushNudge())
  }

  // Fire the native permission prompt inside the click gesture (see
  // promptForPushPermission), THEN let the saga register the device/token.
  const onEnable = () => {
    dispatch(
      pushEvent(NEWDASHBOARD_EVENTS.LIVE_UPDATES, {
        action: LIVE_UPDATES_ACTION.ENABLE_CLICKED,
        source: 'nudge'
      })
    )
    promptForPushPermission()
      .then((permission) => {
        if (permission === 'granted' || permission === 'denied') {
          dispatch(
            pushEvent(NEWDASHBOARD_EVENTS.LIVE_UPDATES, {
              action:
                permission === 'granted'
                  ? LIVE_UPDATES_ACTION.PERMISSION_GRANTED
                  : LIVE_UPDATES_ACTION.PERMISSION_DENIED,
              source: 'nudge'
            })
          )
        }
      })
      .finally(() => dispatch(acceptPushNudge()))
  }

  return (
    <div
      className="okc-push-overlay"
      style={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('dashboard:Enable live updates')}
      onClick={onDismiss}
    >
      <style>{bellAnimationCss}</style>
      <div className="okc-push-card" style={styles.card} onClick={(e) => e.stopPropagation()}>
        <div style={styles.iconWrap} aria-hidden="true">
          <span className="okc-push-bell" style={styles.icon}>
            🔔
          </span>
        </div>

        <div style={styles.title}>{t('dashboard:Get live updates')}</div>
        <div style={styles.subtitle}>
          {t(
            'dashboard:Turn on notifications and your ledger stays up to date automatically — no more hitting refresh to spot new transactions.'
          )}
        </div>

        <ul style={styles.benefits}>
          <li style={styles.benefitRow}>
            <span style={styles.check}>
              <TickSvg width={14} height={14} />
            </span>
            <span>
              {t('dashboard:See payments and dues the moment they’re added on any device')}
            </span>
          </li>
          <li style={styles.benefitRow}>
            <span style={styles.check}>
              <TickSvg width={14} height={14} />
            </span>
            <span>{t('dashboard:Your customer and supplier lists refresh on their own')}</span>
          </li>
          <li style={styles.benefitRow}>
            <span style={styles.check}>
              <TickSvg width={14} height={14} />
            </span>
            <span>{t('dashboard:Get notified even when this tab is in the background')}</span>
          </li>
        </ul>

        <button type="button" style={styles.enableBtn} onClick={onEnable}>
          {t('dashboard:Enable live updates')}
        </button>
        <button type="button" style={styles.dismissBtn} onClick={onDismiss}>
          {t('dashboard:Not now')}
        </button>

        <div style={styles.note}>
          {t(
            'dashboard:Your browser will ask for notification permission. You can turn this off any time from Settings.'
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Keyframes can't live in React inline styles, so the modal's entrance (backdrop
 * fade + card scale-in) and the bell's "gentle ring" swing live in a scoped
 * stylesheet. All motion is paused for users who prefer reduced motion.
 */
const bellAnimationCss = `
  .okc-push-overlay {
    animation: okcPushFadeIn 0.2s ease-out both;
  }
  .okc-push-card {
    animation: okcPushCardIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  @keyframes okcPushFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes okcPushCardIn {
    from { opacity: 0; transform: translateY(12px) scale(0.94); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .okc-push-bell {
    display: inline-block;
    transform-origin: 50% 12%;
    animation: okcPushBellRing 2.6s ease-in-out infinite;
  }
  @keyframes okcPushBellRing {
    0%, 55%, 100% { transform: rotate(0deg); }
    58% { transform: rotate(16deg); }
    62% { transform: rotate(-13deg); }
    66% { transform: rotate(10deg); }
    70% { transform: rotate(-7deg); }
    74% { transform: rotate(4deg); }
    78% { transform: rotate(0deg); }
  }
  @media (prefers-reduced-motion: reduce) {
    .okc-push-overlay,
    .okc-push-card,
    .okc-push-bell { animation: none; }
  }
`

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    boxSizing: 'border-box'
  },
  card: {
    width: 440,
    maxWidth: '100%',
    maxHeight: 'calc(100vh - 48px)',
    overflowY: 'auto',
    background: THEME.brandWhite,
    borderRadius: 20,
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
    padding: '32px 28px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    boxSizing: 'border-box'
  },
  iconWrap: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: THEME.brandLightGreen,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20
  },
  icon: { fontSize: 34, lineHeight: 1 },
  title: { fontSize: 22, fontWeight: 700, color: '#0F172A', marginBottom: 10 },
  subtitle: { fontSize: 15, lineHeight: 1.5, color: '#475569', marginBottom: 20 },
  benefits: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 24px 0',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  benefitRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: 14,
    lineHeight: 1.45,
    color: '#334155',
    textAlign: 'left'
  },
  check: {
    flex: '0 0 auto',
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: THEME.brandLightGreen,
    color: '#16A34A',
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1
  },
  enableBtn: {
    border: 'none',
    background: '#22C55E',
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 600,
    padding: '14px 16px',
    borderRadius: 12,
    cursor: 'pointer',
    width: '100%'
  },
  dismissBtn: {
    border: 'none',
    background: 'transparent',
    color: '#64748B',
    fontSize: 15,
    fontWeight: 500,
    padding: '12px',
    marginTop: 4,
    cursor: 'pointer',
    width: '100%'
  },
  note: {
    fontSize: 12,
    lineHeight: 1.4,
    color: '#94A3B8',
    marginTop: 8
  }
}

export default PushNudge
